import { DestroyRef, Injectable, inject, signal } from '@angular/core';

export type Theme = 'light' | 'dark';

/** localStorage key of the explicit choice. index.html reads the same key before the app boots. */
export const THEME_STORAGE_KEY = 'chroniq_portal_theme';

const DARK_QUERY = '(prefers-color-scheme: dark)';

/**
 * Light / dark theme of the portal. The theme is the `data-theme` attribute on <html>, which
 * switches the colour tokens in styles.scss. Until the user picks one, the operating system's
 * setting decides and is followed live; an explicit choice is persisted and wins from then on.
 *
 * index.html applies the stored or system theme before the first paint, so the page does not
 * flash light on a dark setup; this service takes over from there.
 */
@Injectable({ providedIn: 'root' })
export class ThemeService {
  private readonly systemDark = typeof window.matchMedia === 'function' ? window.matchMedia(DARK_QUERY) : null;

  readonly theme = signal<Theme>(this.initialTheme());

  constructor() {
    this.applyToDocument(this.theme());

    if (this.systemDark) {
      const media = this.systemDark;
      const onChange = (event: MediaQueryListEvent): void => {
        if (!this.readStored()) {
          this.setTheme(event.matches ? 'dark' : 'light', false);
        }
      };
      media.addEventListener('change', onChange);
      inject(DestroyRef).onDestroy(() => media.removeEventListener('change', onChange));
    }
  }

  toggle(): void {
    this.setTheme(this.theme() === 'dark' ? 'light' : 'dark', true);
  }

  private setTheme(theme: Theme, persist: boolean): void {
    this.theme.set(theme);
    this.applyToDocument(theme);
    if (persist) {
      try {
        localStorage.setItem(THEME_STORAGE_KEY, theme);
      } catch {
        // Storage blocked (private mode, policy): the choice then only lasts for this session.
      }
    }
  }

  private initialTheme(): Theme {
    return this.readStored() ?? (this.systemDark?.matches ? 'dark' : 'light');
  }

  private readStored(): Theme | null {
    try {
      const stored = localStorage.getItem(THEME_STORAGE_KEY);
      return stored === 'dark' || stored === 'light' ? stored : null;
    } catch {
      return null;
    }
  }

  private applyToDocument(theme: Theme): void {
    document.documentElement.setAttribute('data-theme', theme);
  }
}
